import { useApolloClient } from '@apollo/client'
import { useCallback } from 'react'

import { useQueryCurrentIteration, useQueryStories } from '@/requests/queries/useQueryProject.ts'

export const useUpdateCache = () => {
  const { cache } = useApolloClient()

  return {
    updateStory: useCallback(
      (storyId: string, params: Partial<Story>) => {
        cache.modify({
          id: cache.identify({
            __typename: 'Story',
            id: storyId,
          }),
          fields: Object.entries(params).reduce(
            (acc, [key, value]) => {
              acc[key] = () => value
              return acc
            },
            {} as Record<string, () => unknown>,
          ),
        })
      },
      [cache],
    ),
  }
}

export const useRecalculateCurrentIterationVelocity = () => {
  const { cache } = useApolloClient()
  const { currentIteration } = useQueryCurrentIteration()
  const { stories: doneStories } = useQueryStories('done')

  return {
    recalculateCurrentIterationVelocity: useCallback(() => {
      if (currentIteration.number < 4) return 10

      const pastThreeIterationEstimate = doneStories
        .filter(
          (story) =>
            story.iterationNumber &&
            story.iterationNumber < currentIteration.number &&
            story.iterationNumber >= currentIteration.number - 3,
        )
        .reduce((acc, { estimate }) => acc + (estimate === -1 ? 0 : estimate), 0)

      const newVelocity = Math.floor(pastThreeIterationEstimate / 3)

      cache.modify({
        id: cache.identify({
          __typename: 'Iteration',
          number: currentIteration.number,
          projectId: currentIteration.projectId,
        }),
        fields: {
          velocity: () => newVelocity,
        },
      })
    }, [currentIteration, doneStories, cache]),
  }
}
